import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/authStore.tsx';
import { ApiError } from '../../lib/apiClient.tsx';
import { strings } from '../../lib/strings.tsx';
import './LoginScreen.css';

/**
 * 로그인 실패 사유를 **상태 코드로** 정한다. 서버 문구를 그대로 쓰지 않는 이유:
 *
 *  1. 서버 문구는 짧은 코드성 영어이고("invalid username or password") 사람이 읽을
 *     문장이 아니다 — 화면에는 완결된 문장을 보여준다(지시21 이후에도 유효한 이유).
 *  2. **빈 문구가 나올 수 있다.** 본문이 없는 401(프록시·엣지가 대신 답하는 경우)
 *     이면 apiClient 가 `res.statusText` 로 떨어지는데, HTTP/2 에서는 그 값이
 *     항상 빈 문자열이다. 그러면 화면에 **빈 경고 상자**가 떠 사용자 눈에는
 *     아무 말도 안 하는 것과 같다 — 조용한 실패다(결정38 과 같은 부류).
 *
 * 아이디와 비밀번호를 구분해 알리지 않는다. 계정 존재 여부가 새면 안 되고,
 * 서버도 같은 이유로 같은 문구를 준다(03 §11.2).
 */
export function describeLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return strings.login.errors.badCredentials;
    if (err.status === 429) return strings.login.errors.tooManyAttempts;
    if (err.status >= 500) return strings.login.errors.serverError(err.status);
    return strings.login.errors.generic(err.status);
  }
  // 네트워크가 끊겼거나 서버에 닿지 못한 경우 — 비밀번호 문제가 아니므로 구분해 알린다.
  return strings.login.errors.network;
}

/**
 * 콘솔의 유일한 인증 화면(결정46). 콘솔은 API 키를 다루지 않으므로 사람은
 * 아이디·비밀번호로만 들어온다. 성공하면 AuthProvider가 세션 쿠키로 로그인
 * 상태를 판정해 이 화면 대신 실제 콘솔을 그린다(App.tsx가 status로 갈라 그린다).
 *
 * **로그인하면 Dashboard 로 간다** (지시194). 이 화면은 미인증일 때 **지금 주소 그대로**
 * 그려진다 — 그래서 보던 탭이 `/operator` 였으면 로그인 뒤에도 Operator 가 떴다
 * 〔운영자 실기 2026-09-15〕. 콘솔 밖에서 콘솔 안쪽으로 거는 링크는 없다(서버 문구 전수).
 */
export function LoginScreen() {
  const { login, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(describeLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-screen__card" onSubmit={(e) => void handleSubmit(e)}>
        <div className="login-screen__brand">
          <span className="login-screen__mark" role="img" aria-label={strings.layout.brand} />
          <h1 className="login-screen__title">{strings.layout.brand}</h1>
        </div>
        <p className="login-screen__product">{strings.layout.product}</p>
        <p className="login-screen__subtitle">{strings.login.subtitle}</p>
        {/* 쫓겨나 온 사람에게만. 그냥 방문한 사람에게는 뜻 없는 겁주기다. */}
        {sessionExpired && (
          <p className="login-screen__expired" role="status">
            {strings.login.sessionExpired}
          </p>
        )}

        <label className="login-screen__field" htmlFor="login-username">
          {strings.login.username}
          <input
            id="login-username"
            name="username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </label>

        <label className="login-screen__field" htmlFor="login-password">
          {strings.login.password}
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        {/* 빈 문자열이면 그리지 않는다. 빈 경고 상자는 '아무 말도 안 하는' 것과 같다. */}
        {error !== null && error.trim() !== '' && (
          <div role="alert" className="login-screen__error">
            {error}
          </div>
        )}

        <button type="submit" className="login-screen__submit" disabled={submitting}>
          {submitting ? strings.login.signingIn : strings.login.signIn}
        </button>
      </form>
    </div>
  );
}
